import React, { useState } from 'react';

import { PopoverPlacement } from '@rover/components/src/callouts/Popover/popover.constants';
import { useEnableSchema } from '@rover/components/src/microdata';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import SitterBadgeComponent from '@rover/react-lib/src/pages/search/shared/components/SitterBadge';
import { isReactNative } from '@rover/rsdk/src/modules/Env/react';
import type { CommonSearchResult } from '@rover/types/src/CommonSearchResult';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';

import { STAR_SITTER } from '../../../callouts/CalloutBadge/CalloutBadge.constants';
import SimplifiedPriceColumn from '../PriceColumn/SimplifiedPriceColumn';
import { SEARCH_RESULT_CARD_Z_INDEX, SITTER_NAME_MAX_LENGTH } from '../SearchResultCard.constants';

export type Props = {
  searchResult: CommonSearchResult;
  showPrice: boolean;
  locale: Locale;
  showFromText: boolean;
  isBHUser: boolean;
  shouldShowPriceInCredits: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
};

const truncateContent = (content: string, length: number): string => {
  const normalizedContent = content.trim();
  if (normalizedContent.length > length + 3) {
    return `${normalizedContent.substring(0, length)}...`;
  }
  return normalizedContent;
};

const SimplifiedNameSection: React.FC<Props> = ({
  searchResult: {
    currencyCode,
    price,
    priceInCredits,
    unitAsSentence,
    caption,
    shortName,
    isStarSitter,
    primaryBadgeData,
  },
  showPrice,
  locale,
  showFromText,
  isBHUser,
  shouldShowPriceInCredits,
  priceTransparencyLevel,
}) => {
  const enableSchema = useEnableSchema();

  const badgeList = [
    isStarSitter ? (
      <Badge
        key="star-sitter"
        {...STAR_SITTER}
        boxProps={{
          ...STAR_SITTER.boxProps,
          webProps: enableSchema ? { itemProp: 'award' } : {},
        }}
      />
    ) : null,
  ].filter(Boolean);

  const calloutBadges =
    badgeList.length > 0 ? (
      <Flex gap="2x" flexWrap="wrap">
        {badgeList}
      </Flex>
    ) : null;

  const [isBadgeOpen, setIsBadgeOpen] = useState(false);

  // The background-check badge is not shown on the apps; web still renders it.
  const inlineBadge =
    !isReactNative() && primaryBadgeData ? (
      <Box
        minWidth="auto"
        zIndex={
          isBadgeOpen
            ? SEARCH_RESULT_CARD_Z_INDEX.BADGE_OPEN.toString()
            : SEARCH_RESULT_CARD_Z_INDEX.BADGE_CLOSED.toString()
        }
      >
        <SitterBadgeComponent
          title={primaryBadgeData.title}
          content={primaryBadgeData.content}
          imageUrl={primaryBadgeData.imageUrl}
          slug={primaryBadgeData.slug}
          width={24}
          popoverPlacement={PopoverPlacement.RIGHT}
          onChange={setIsBadgeOpen}
          {...(enableSchema ? { itemProp: 'hasCredential' } : {})}
        />
      </Box>
    ) : null;

  const sitterName = (
    <Heading
      aria-label={shortName}
      mb="0x"
      size="300"
      fontWeight="semibold"
      sx={{
        overflowWrap: 'anywhere',
        wordBreak: 'break-all',
      }}
      {...(enableSchema ? { itemProp: 'name' } : {})}
    >
      {truncateContent(shortName, SITTER_NAME_MAX_LENGTH)}
    </Heading>
  );

  const priceElement = showPrice ? (
    <SimplifiedPriceColumn
      currencyCode={currencyCode}
      locale={locale}
      price={price}
      priceInCredits={priceInCredits}
      shouldShowPriceInCredits={shouldShowPriceInCredits}
      showFromText={showFromText}
      unitAsSentence={unitAsSentence}
      priceTransparencyLevel={priceTransparencyLevel}
      isBHUser={isBHUser}
      variant="minimal"
    />
  ) : null;

  // Mobile: Callouts first (separate row), name with inline badge, then location
  const mobileLayout = (
    <Flex flexDirection="column" width="100%" gap="2x">
      <Flex
        flexDirection="row"
        gap="2x"
        alignItems="center"
        flexWrap="wrap"
        maxWidth={showPrice ? '100%' : '70%'}
      >
        {sitterName}
        {inlineBadge}
        {calloutBadges}
      </Flex>
      <Text size="100">{caption}</Text>
      {priceElement}
    </Flex>
  );

  // Web: Name, callouts, and background check badge inline
  // Disable rendering RxN to avoid being read by screen readers and improve performance.
  const webLayout = isReactNative() ? null : (
    <Flex flexDirection="row" gap="2x" flexWrap="nowrap" alignItems="flex-start">
      <Flex maxWidth="70%" flexDirection="row" gap="2x" flexWrap="wrap">
        {sitterName}
        {inlineBadge}
        {calloutBadges}
      </Flex>
      {priceElement}
    </Flex>
  );

  return (
    <>
      <Box display={['block', 'none']}>{mobileLayout}</Box>
      <Box display={['none', 'block']}>{webLayout}</Box>
    </>
  );
};

export default SimplifiedNameSection;
